<template>
  <div class="relative h-full" ref="screenerRef">
    <BaseTable
      class="h-full"
      :column-defs="columnDefs"
      :row-height="35"
      :row-data="rowData"
      :get-row-id="getRowId"
      :tooltip-show-delay="0"
      :default-col-def="updatedDefaultColDef"
      :animate-rows="false"
      :row-buffer="50"
      :key="`${rowData?.length}_${columnDefs?.length}`"
      :debounce-vertical-scrollbar="false"
      @grid-ready="onGridReady"
      @sort-changed="sortChanged"
      @body-scroll="scrollPosition = getScrollFromTop(gridApi)"
    />
    <BaseButton
      v-if="scrollPosition > 500"
      class="absolute bottom-5 right-5 w-10"
      size="sm"
      @press="scrollToTop(gridApi)"
    >
      <IconArrowUp />
    </BaseButton>
  </div>
</template>
<script setup>
import { useStore } from 'vuex';
import { isCoinVerified } from '@/composeables/helpers';
import { computed, ref, watch, onMounted, onBeforeUnmount, inject, nextTick } from 'vue';
import { numberComparator, textComparator } from '@/composeables/ag_grid/comparators.js';
import { getScrollFromTop, scrollToTop } from '@/composeables/ag_grid/shared_methods.js';
import ScreenerBigNumberCell from '@/components/screener/ScreenerBigNumberCell.vue';
import ScreenerSentimentCell from '@/components/screener/ScreenerSentimentCell.vue';
import ScreenerMenuCell from '@/components/screener/ScreenerMenuCell.vue';
import ScreenerPriceCell from '@/components/screener/ScreenerPriceCell.vue';
import ScreenerDollarCell from '@/components/screener/ScreenerDollarCell.vue';
import ScreenerBtcCell from '@/components/screener/ScreenerBtcCell.vue';
import ScreenerNumberCell from '@/components/screener/ScreenerNumberCell.vue';
import ScreenerIndexCell from '@/components/screener/ScreenerIndexCell.vue';
import AgGridBaseCellCoin from '@/components/ag_grid/AgGridBaseCellCoin.vue';
import AgGridWidgetCoinCell from '@/components/ag_grid/AgGridWidgetCoinCell.vue';
import AgGridTooltip from '@/components/ag_grid/AgGridTooltip.vue';
import ScreenerSectorCell from '@/components/screener/ScreenerSectorCell.vue';
import ScreenerEcosystemCell from '@/components/screener/ScreenerEcosystemCell.vue';

const emits = defineEmits(['on:grid-ready', 'update:sort']);
const $store = useStore();
const props = defineProps({
  entities: { type: Array, required: true, default: () => [] },
  config: { type: Object, required: true }
});

//GRID
const gridApi = ref(null);
const columnApi = ref(null);
const scrollPosition = ref(0);
const screenerRef = ref(null);
const screenerPage = inject('screener-page', 'coins');
const loading = computed(() => {
  return $store.getters.coinsLoader;
});
const screenerColumn = computed(() => {
  return `${screenerPage}_screener_sortCol`;
});
const screenerOrder = computed(() => {
  return `${screenerPage}_screener_sortOrder`;
});
const onGridReady = params => {
  emits('on:grid-ready', params);
  gridApi.value = params.api;
  columnApi.value = params.columnApi;
  applyTableSort();
  screenerRef.value.onresize = () => {
    if (columnApi.value?.columnModel?.columnDefs?.length >= 10) {
      columnApi.value.autoSizeAllColumns();
    }
  };
};
const sortChanged = params => {
  const sortColumn = params.columnApi.getColumnState().find(c => c.sort);
  if (props.config.widget) {
    emits('update:sort', { colId: sortColumn?.colId, order: sortColumn?.sort });
  } else {
    localStorage.setItem(screenerColumn.value, JSON.stringify(sortColumn?.colId));
    localStorage.setItem(screenerOrder.value, JSON.stringify(sortColumn?.sort));
  }

  const sortedRows = [];
  const renderedNodes = gridApi.value?.getRenderedNodes() || [];
  let entities = props.entities;
  // Iterate over the rendered nodes to get the sorted rows.
  renderedNodes.forEach(node => {
    sortedRows.push(node.data);
  });
  let unsortedEntities =
    screenerPage == 'coins'
      ? entities?.filter(i => !sortedRows?.map(j => j.coin_uid).includes(i.uid))
      : entities?.filter(i => !sortedRows?.map(j => j.id).includes(i.id));
  entities = sortedRows && sortedRows.length ? sortedRows?.concat(unsortedEntities) : entities;
  assignEntitiesData(entities);

  gridApi.value.refreshCells();
};
const getRowId = data => {
  return data.data.id;
};
function resizeTable() {
  if (gridApi.value) {
    gridApi.value.sizeColumnsToFit();
  }
}
function getSortConfig() {
  let sortConfig = {};
  let columns = props.config.cols?.map(c => c.id);
  if (props.config.widget) {
    sortConfig = props.config.sort;
  } else {
    const cachedCol = window.localStorage[screenerColumn.value];
    const cachedOrder = window.localStorage[screenerOrder.value];
    sortConfig = {
      colId: cachedCol && cachedCol !== 'undefined' ? JSON.parse(cachedCol) : 'market_cap',
      order: cachedOrder && cachedOrder !== 'undefined' ? JSON.parse(cachedOrder) : 'desc'
    };
  }
  return columns?.includes(sortConfig?.colId) ? sortConfig : { colId: 'market_cap', order: 'desc' };
}
function applyTableSort() {
  if (columnApi.value) {
    nextTick(() => {
      const sortConfig = getSortConfig();
      columnApi.value.applyColumnState({
        state: [{ sort: undefined }]
      });
      columnApi.value.applyColumnState({
        state: [
          {
            colId: sortConfig.colId || 'market_cap',
            sort: sortConfig.order || 'desc'
          }
        ]
      });
    });
  }
}
onMounted(() => {
  screenerRef.value.addEventListener('resize', resizeTable);
  if (!props.config.widget && columnApi.value) {
    columnApi.value.autoSizeAllColumns();
  }
});
onBeforeUnmount(() => {
  screenerRef.value.removeEventListener('resize', resizeTable);
});

//TABLE CONFIG
const minCellWidth = computed(() => {
  return props.config.widget ? 90 : 175;
});
const defaultColDef = computed(() => {
  return {
    sortable: true,
    filter: false,
    sortingOrder: ['desc', 'asc'],
    tooltipComponent: AgGridTooltip,
    comparator: numberComparator,
    minWidth: props.config.compact ? null : minCellWidth.value,
    suppressDragLeaveHidesColumns: true,
    lockPosition: 'left'
  };
});
const updatedDefaultColDef = computed(() => {
  //get maximum possible number of columns to adjust
  const cols = props.config.widget ? props.config.width + 100 / minCellWidth.value : 10;
  if (props.config.compact || columnDefs.value.length < cols) {
    return {
      ...defaultColDef.value,
      flex: '1'
    };
  } else {
    return defaultColDef.value;
  }
});
const columnDefs = computed(() => {
  let config = props.config;
  let cols = [];
  cols = config.cols?.map(i => {
    config = {};
    config['cellStyle'] = { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-end' };
    config['cellClass'] = 'tabular-nums';
    if (i.type == 'index') {
      i.id = 'menu';
      config['cellRenderer'] = ScreenerMenuCell;
      config['pinned'] = 'left';
      config['type'] = 'rightAligned';
      config['cellStyle'] = { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-start' };
      i.name = '';
      config['maxWidth'] = 30;
      config['minWidth'] = 30;
    } else if (i.type == 'coin' || i.type == 'widget_coin') {
      i.id = 'coin';
      config['cellRenderer'] = i.type == 'widget_coin' ? AgGridWidgetCoinCell : AgGridBaseCellCoin;
      config['pinned'] = i.type == 'widget_coin' ? null : 'left';
      config['tooltipValueGetter'] = params => {
        return getCoinTooltipContent(params);
      };
      config['comparator'] = (valueA, valueB, nodeA, nodeB, isDescending) => {
        return valueA == valueB ? 0 : valueA?.name > valueB?.name ? 1 : -1;
      };
      config['minWidth'] = 200;
      config['cellStyle'] = { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-start' };
      config['cellRendererParams'] = {
        referrer: props.config.widget,
        width: props.config.width || null
      };
    } else if (i.type == 'sector') {
      config['pinned'] = 'left';
      config['cellStyle'] = { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-start' };
      config['cellRenderer'] = ScreenerSectorCell;
      config['cellRendererParams'] = {
        referrer: props.config.widget
      };
      config['minWidth'] = 220;
      config['maxWidth'] = 300;
      config['comparator'] = textComparator;
    } else if (i.type == 'ecosystem') {
      config['pinned'] = 'left';
      config['cellRenderer'] = ScreenerEcosystemCell;
      config['cellRendererParams'] = {
        referrer: props.config.widget
      };
      config['cellStyle'] = { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-start' };
      config['comparator'] = textComparator;
    } else if (i.type == 'percent') {
      config['cellRenderer'] = 'BaseCellPercentChange';
      config['type'] = 'rightAligned';
      config['valueGetter'] = params => (params.data[i.id] ? parseFloat(params.data[i.id]) * 100 : null);
    } else if (i.type == 'number') {
      config['type'] = 'rightAligned';
      config['cellRendererParams'] = { decimals: i.decimals };
      config['cellRenderer'] = ScreenerNumberCell;
      config['valueGetter'] = params => (params.data[i.id] ? parseFloat(params.data[i.id]) : null);
      config['comparator'] = numberComparator;
    } else if (i.type == 'btc_format') {
      config['type'] = 'rightAligned';
      config['cellRenderer'] = ScreenerBtcCell;
    } else if (i.type == 'dollar' || i.type == 'price') {
      config['cellRenderer'] = i.id == 'price' ? ScreenerPriceCell : ScreenerDollarCell;
      config['type'] = 'rightAligned';
    } else if (i.type == 'dollar_format' || i.type == 'million_format') {
      config['cellRenderer'] = ScreenerBigNumberCell;
      if (i.type == 'dollar_format') {
        config['cellRendererParams'] = { dollar_format: true };
      }
      config['type'] = 'rightAligned';
      config['valueGetter'] = params => (params.data[i.id] ? parseFloat(params.data[i.id]) : null);
      config['comparator'] = numberComparator;
    } else if (i.type == 'simple_percent') {
      config['type'] = 'rightAligned';
      config['valueGetter'] = params => (params.data[i.id] ? parseFloat(params.data[i.id]) * 100 : null);
      config['cellRenderer'] = 'BaseCellPercent';
    } else if (i.type == 'sentiment') {
      config['type'] = 'rightAligned';
      config['cellRenderer'] = ScreenerSentimentCell;
    }
    return {
      headerName: i.name,
      tooltipComponentParams: { html: true },
      headerTooltip:
        i.id == 'coin'
          ? ''
          : `<div class="text-xs space-y-1"><span class="font-bold block">${i.name}</span><span class="block">${
              i?.definition || '-'
            }</span>`,
      field: i.id,
      ...config
    };
  });

  if (!props.config.noIndex) {
    const indexNumber = {
      headerName: '#',
      field: 'indexVal',
      cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'flex-end' },
      maxWidth: 45,
      minWidth: 40,
      cellRenderer: ScreenerIndexCell,
      type: 'rightAligned',
      pinned: 'left'
    };
    if (cols.length) {
      cols.splice(screenerPage == 'coins' ? 1 : 0, 0, indexNumber);
    }
  }
  return cols;
});

//TABLE DATA
const rowData = ref(null);
const assignEntitiesData = entities => {
  if (loading.value) return;
  rowData.value = entities.map((i, index) => {
    return screenerPage == 'coins'
      ? {
          ...i,
          indexVal: index,
          menu: {
            id: i['id'],
            index: index,
            name: i['name'],
            uid: i['uid'],
            watchlist_ids: i['watchlist_ids'],
            noLink: i['noLink']
          },
          coin: {
            id: i?.id,
            name: i?.name,
            uid: i?.coin_uid,
            coin_uid: i?.coin_uid,
            ticker: i['ticker'],
            verified: isCoinVerified(i.coin_uid),
            href: '/coin/' + i?.coin_uid
          }
        }
      : { ...i, indexVal: index };
  });
};
onMounted(() => {
  if (props.entities.length > 0) {
    assignEntitiesData(props.entities);
  }
});
function getCoinTooltipContent(params) {
  if (params.value.verified) {
    return `
    <div class="max-w-md break-words px-2 text-left p-1">
      <div class="mb-0.5 flex items-center space-x-1 font-bold normal-case text-white">
        <span class="block">Asset Verified</span>
        <IconVerified :size="'h-3 w-3'" />
      </div>
      <div class="whitespace-normal break-words font-semibold normal-case text-gray-300">
        The Tie team has verified <span class="font-bold normal-case text-white">${params.value.name}</span> token.
      </div>
    </div>
  `;
  } else {
    return params.value.name;
  }
}
const setRowData = () => {
  assignEntitiesData(props.entities);
  gridApi.value?.sizeColumnsToFit();
};
function arraysAreSame(a, b) {
  let col = { coins: 'name', sectors: 'sector', ecosystems: 'ecosystem' }[screenerPage];
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((x, idx) => x[col] == b[idx][col] && x.y == b[idx].y)
  );
}
watch(
  () => props.entities,
  (newVal, oldVal) => {
    if (!arraysAreSame(newVal, oldVal)) {
      setRowData();
    }
  },
  { immediate: true, deep: true }
);
watch(columnDefs, () => {
  if (gridApi.value) {
    gridApi.value.setColumnDefs(columnDefs.value);
    gridApi.value.sizeColumnsToFit();
    applyTableSort();
  }
});
</script>
